import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { Box, Button, TextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress, Container } from "@mui/material";
import ProjectTable from "../../components/order/project/ProjectTable";
import PageTitle from "../../components/common/pages/PageTitle";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { SortType } from "../../../models/SortType";
import { ProjectRequestFilters } from "../../../models/Project/ProjectRequestFilters";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";
import { OrderType } from "../../../models/Project/OrderType";
import { PaymentStatuses } from "../../../models/Project/PaymentStatus";
import AppDropDown from "../../components/common/filter/AppDropDown";
import { ProjectRequestStatus } from "../../../models/Project/ProjectRequestStatus";
import { observer } from "mobx-react";
import ConfirmationPopup from "../../components/common/pop-up/ConfirmationPopup";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";

interface ProjectRequestSelectedFilters {
  order_id: FilterValue[];
  status: FilterValue[];
  order_type: FilterValue[];
  payment_status: FilterValue[];
  active: boolean;
}

function ProjectRequests() {
  const { projectStore, orderStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();

  const location = useLocation();
  const navigate = useNavigate();

  const [selectedId, setSelectedId] = useState<number>();
  const [openApprove, setOpenApprove] = useState<boolean>();
  const [openReject, setOpenReject] = useState<boolean>();

  const searchParams = new URLSearchParams(location.search);

  const initialParams: ProjectRequestFilters = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    order_id: searchParams.get("order_id")
      ? searchParams.get("order_id")?.split(",").map(Number)
      : [],
    order_type: searchParams.get("order_type")
      ? searchParams.get("order_type")?.split(",").map(Number)
      : [],
    payment_status: searchParams.get("payment_status")
      ? searchParams.get("payment_status")?.split(",").map(Number)
      : [],

    status: searchParams.get("status")
      ? searchParams.get("status")?.split(",").map(Number)
      : [],
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
  };

  const initialFilters: ProjectRequestSelectedFilters = {
    order_id: searchParams.get("order_id")
      ? searchParams
        .get("order_id")!
        .split(",")
        .map((id) => ({ id: id, value: id }))
      : [],
    order_type: searchParams.get("order_type")
      ? searchParams
        .get("order_type")!
        .split(",")
        .map((item) => {
          const list = item.split(":")!;
          return { id: list[0], value: list[1] };
        })
      : [],
    payment_status: searchParams.get("payment_status")
      ? searchParams
        .get("payment_status")!
        .split(",")
        .map((id) => ({ id: id, value: id }))
      : [],
    status: searchParams.get("status")
      ? searchParams
        .get("status")!
        .split(",")
        .map((id) => ({ id: id, value: id }))
      : [],
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
  };

  const [filters, setFilters] =
    useState<ProjectRequestSelectedFilters>(initialFilters);
  const [params, setParams] = useState(initialParams);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );

  let apiTimer: any;

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_ORDER)) {
      setIsLoading(true);
      projectStore
        .getProjectRequestList(params)
        .catch((reason) =>
          showError(
            "Error!",
            reason?.response?.data?.message ||
            "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value && (!Array.isArray(value) || value.length > 0)) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
  }, [params, navigate]);

  const handleSearchBar = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.length !== 0) {
      setSearchTerm(e.target.value);
    } else {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    setParams((prev) => ({
      ...prev,
      search_text: searchTerm,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handleApply = () => {
    setParams((prev) => ({
      ...prev,
      order_id: filters.order_id.map((item) => Number(item.id)),
      order_type: filters.order_type.map((item) => Number(item.id)),
      payment_status: filters.payment_status.map((item) => Number(item.id)),
      status: filters.status.map((item) => Number(item.id)),
      active: filters.active,
    }));
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilters({
      order_id: [],
      order_type: [],
      payment_status: [],
      status: [],
      active: true,
    });
    setParams({
      page_no: 1,
      limit: 30,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      order_id: [],
      order_type: [],
      payment_status: [],
      status: [],
      active: true,
    });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => { });
      }, 1500);
  };

  return (
    <Container maxWidth="xl">
      <PageTitle title="Project Request List" />
      {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
        <>
          {isLoading ? (
            <Container
              sx={{
                minHeight: "60vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          ) : (
            <Box>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    paddingBottom: 3,
                    justifyContent: "right",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <TextField
                      id="standard-search"
                      placeholder="Brand Name"
                      type="search"
                      variant="outlined"
                      value={searchTerm}
                      onChange={(e) => handleSearchBar(e)}
                      onKeyDown={(e) => handleKeyDown(e)}
                      size="medium"
                      sx={{
                        minWidth: "200px",
                        "& .MuiOutlinedInput-input": {
                          padding: "6px 10px",
                          fontSize: "12px",
                        },
                      }}
                    />
                    <Button
                      sx={{
                        minWidth: "80px",
                        padding: "6px 10px",
                        fontSize: "12px",
                      }}
                      variant="contained"
                      onClick={handleSearchSubmit}
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: { xs: "wrap", md: "nowrap" },
                    justifyContent: { xs: "center", md: "end" },
                    alignItems: "center",
                    gap: 2,
                    paddingBottom: 2,
                    maxHeight: { xs: "none", md: 40 },
                  }}
                >
                  <Box
                    sx={{
                      width: 200,
                    }}
                  >
                    <AppSearchDropDown
                      isMultiSelect={true}
                      title="Order Id"
                      value={filters.order_id}
                      fontSize="12px"
                      list={orderStore.orderIds.map((item) => {
                        return { id: String(item), value: String(item) };
                      })}
                      local={false}
                      handleSearch={handleOrderIdSearch}
                      handleSelect={(items) => {
                        setFilters((prev) => ({
                          ...prev,
                          order_id: items,
                        }));
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      width: 200,
                    }}
                  >
                    <AppDropDown
                      title=""
                      isMultiSelect={true}
                      showPlaceholder={true}
                      placeholderText="Select Order Type"
                      valuesList={filters.order_type.map((item) => item.id)}
                      list={OrderType}
                      fontSize={"12px"}
                      onMultiSelectionChange={(ids) => {
                        setFilters((prev) => ({
                          ...prev,
                          order_type: OrderType.filter((item) =>
                            ids.includes(item.id)
                          ),
                        }));
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: 200,
                    }}
                  >
                    <AppDropDown
                      title=""
                      isMultiSelect={true}
                      showPlaceholder={true}
                      placeholderText="Select Payment Status"
                      valuesList={filters.payment_status.map((item) => item.id)}
                      list={PaymentStatuses}
                      fontSize={"12px"}
                      onMultiSelectionChange={(ids) => {
                        setFilters((prev) => ({
                          ...prev,
                          payment_status: PaymentStatuses.filter((item) =>
                            ids.includes(item.id)
                          ),
                        }));
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: 200,
                    }}
                  >
                    <AppDropDown
                      title=""
                      isMultiSelect={true}
                      showPlaceholder={true}
                      placeholderText="Select Status"
                      valuesList={filters.status.map((item) => item.id)}
                      list={ProjectRequestStatus}
                      fontSize={"12px"}
                      onMultiSelectionChange={(ids) => {
                        setFilters((prev) => ({
                          ...prev,
                          status: ProjectRequestStatus.filter((item) =>
                            ids.includes(item.id)
                          ),
                        }));
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: 200,
                    }}
                  >
                    <AppDropDown
                      title=""
                      isMultiSelect={false}
                      value={filters.active ? "true" : "false"}
                      list={[
                        { id: "true", value: "Active" },
                        { id: "false", value: "InActive" },
                      ]}
                      fontSize={"12px"}
                      onSingleSelectionChange={(id) => {
                        setFilters((prev) => ({
                          ...prev,
                          active: id === "true",
                        }));
                      }}
                    />
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        maxHeight: "34px",
                        padding: "6px 16px",
                        fontSize: "12px",
                      }}
                      onClick={handleApply}
                    >
                      Apply
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      variant="outlined"
                      color="secondary"
                      sx={{
                        maxHeight: "34px",
                        padding: "6px 16px",
                        fontSize: "12px",
                      }}
                      onClick={resetFilters}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </Box>
              <Box>
                {selectedId && (
                  <ConfirmationPopup
                    id={selectedId}
                    title={"Confirmation"}
                    taskAssigned={false}
                    content={
                      "You are going to approve request id " +
                      selectedId +
                      ". Do you want to continue?"
                    }
                    onCancel={() => {
                      setSelectedId(undefined);
                      setOpenApprove(false);
                    }}
                    onConfirm={async (id, _input, _isTaskAssigned) => {
                      try {
                        setOpenApprove(false);
                        await projectStore.approveRequest(
                          id,
                          userStore.user?.id,
                          userStore.user?.name
                        );
                        // Default tasks on approve disabled — createDefaultTasks no longer used here.
                        // if (isTaskAssigned) {
                        //   const selectedRequest =
                        //     projectStore.projectRequest.find(
                        //       (req) => req.id === id
                        //     );
                        //   if (selectedRequest) {
                        //     await projectStore.createDefaultTasks(
                        //       selectedRequest.order_id
                        //     );
                        //   }
                        // }
                        showSuccess("Request approved!");
                      } catch (reason: any) {
                        showError(
                          "Error!",
                          reason?.response?.data?.message ||
                          "Something went wrong! Please try again later."
                        );
                      }
                    }}
                    open={openApprove || false}
                  />
                )}
                {selectedId && (
                  <ConfirmationPopup
                    id={selectedId}
                    title={"Confirmation"}
                    content={
                      "You are going to reject request id " +
                      selectedId +
                      ". Do you want to continue?"
                    }
                    type="form"
                    inputRequired={true}
                    inputLabel="State your reason here"
                    inputType="text"
                    onCancel={() => {
                      setSelectedId(undefined);
                      setOpenReject(false);
                    }}
                    onConfirm={(id, input) => {
                      setOpenReject(false);
                      if (input)
                        projectStore
                          .rejectRequest(id, input)
                          .then(() => {
                            showSuccess("Request reject!");
                            handleApply();
                          })
                          .catch((reason) =>
                            showError(
                              "Error!",
                              reason?.response?.data?.message ||
                              "Something went wrong! Please try again later."
                            )
                          );
                    }}
                    open={openReject || false}
                  />
                )}
                {!isLoading && projectStore.projectRequest.length === 0 ? (
                  <p>No project request found</p>
                ) : undefined}
                {!isLoading && projectStore.projectRequest.length > 0 ? (
                  <ProjectTable
                    project={projectStore.projectRequest || []}
                    limit={params.limit || projectStore.limit}
                    page_no={params.page_no}
                    total_count={projectStore.total_count}
                    total_pages={projectStore.total_pages}
                    sortBy={params.sort_by}
                    sortType={params.sort_type}
                    onSortChange={(column, sortType) => {
                      setParams((prev) => ({
                        ...prev,
                        sort_by: column,
                        sort_type: sortType,
                      }));
                    }}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    handleApprove={(id) => {
                      setSelectedId(id);
                      setOpenApprove(true);
                    }}
                    handleReject={(id) => {
                      setSelectedId(id);
                      setOpenReject(true);
                    }}
                  />
                ) : undefined}
              </Box>
            </Box>
          )}
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </Container>
  );
}

export default observer(ProjectRequests);
